'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'

interface SceneContextSelectorProps {
  savedWorlds: any[]
  communities: any[]
  societies: any[]
  dungeons: any[]
  characters: any[]
  items: any[]
  enemies: any[]
  relationships: any[]
  selectedWorldId: string
  selectedCommunityIds: string[]
  selectedSocietyIds: string[]
  selectedDungeonId: string
  selectedCharacterIds: string[]
  selectedItemIds: string[]
  selectedEnemyIds: string[]
  selectedRelationshipIds: string[]
  onWorldChange: (id: string) => void
  onCommunitiesChange: (ids: string[]) => void
  onSocietiesChange: (ids: string[]) => void
  onDungeonChange: (id: string) => void
  onCharactersChange: (ids: string[]) => void
  onItemsChange: (ids: string[]) => void
  onEnemiesChange: (ids: string[]) => void
  onRelationshipsChange: (ids: string[]) => void
}

export function SceneContextSelector({
  savedWorlds,
  communities,
  societies,
  dungeons,
  characters,
  items,
  enemies,
  relationships,
  selectedWorldId,
  selectedCommunityIds,
  selectedSocietyIds,
  selectedDungeonId,
  selectedCharacterIds,
  selectedItemIds,
  selectedEnemyIds,
  selectedRelationshipIds,
  onWorldChange,
  onCommunitiesChange,
  onSocietiesChange,
  onDungeonChange,
  onCharactersChange,
  onItemsChange,
  onEnemiesChange,
  onRelationshipsChange
}: SceneContextSelectorProps) {
  const [isOpen, setIsOpen] = useState(true)

  const handleCommunityToggle = (communityId: string) => {
    if (selectedCommunityIds.includes(communityId)) {
      onCommunitiesChange(selectedCommunityIds.filter(id => id !== communityId))
    } else {
      onCommunitiesChange([...selectedCommunityIds, communityId])
    }
  }

  const handleSocietyToggle = (societyId: string) => {
    if (selectedSocietyIds.includes(societyId)) {
      onSocietiesChange(selectedSocietyIds.filter(id => id !== societyId))
    } else {
      onSocietiesChange([...selectedSocietyIds, societyId])
    }
  }

  const handleCharacterToggle = (characterId: string) => {
    console.log('【デバッグ】キャラクタートグル:', {
      characterId: characterId,
      現在の選択: selectedCharacterIds,
      追加か削除: selectedCharacterIds.includes(characterId) ? '削除' : '追加'
    })
    
    if (selectedCharacterIds.includes(characterId)) {
      const newIds = selectedCharacterIds.filter(id => id !== characterId)
      console.log('【デバッグ】キャラクター削除後:', newIds)
      onCharactersChange(newIds)
    } else {
      const newIds = [...selectedCharacterIds, characterId]
      console.log('【デバッグ】キャラクター追加後:', newIds)
      onCharactersChange(newIds)
    }
  }

  const handleItemToggle = (itemId: string) => {
    if (selectedItemIds.includes(itemId)) {
      onItemsChange(selectedItemIds.filter(id => id !== itemId))
    } else {
      onItemsChange([...selectedItemIds, itemId])
    }
  }

  const handleEnemyToggle = (enemyId: string) => {
    if (selectedEnemyIds.includes(enemyId)) {
      onEnemiesChange(selectedEnemyIds.filter(id => id !== enemyId))
    } else {
      onEnemiesChange([...selectedEnemyIds, enemyId])
    }
  }

  const handleRelationshipToggle = (relationshipId: string) => {
    console.log('【デバッグ】関係性トグル:', {
      relationshipId: relationshipId,
      現在の選択: selectedRelationshipIds,
      追加か削除: selectedRelationshipIds.includes(relationshipId) ? '削除' : '追加'
    })
    
    if (selectedRelationshipIds.includes(relationshipId)) {
      const newIds = selectedRelationshipIds.filter(id => id !== relationshipId)
      console.log('【デバッグ】関係性削除後:', newIds)
      onRelationshipsChange(newIds)
    } else {
      const newIds = [...selectedRelationshipIds, relationshipId]
      console.log('【デバッグ】関係性追加後:', newIds)
      onRelationshipsChange(newIds)
    }
  }

  return (
    <div className="mb-4">
      <Button 
        variant="outline" 
        className="w-full justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>他のジェネレータ要素を連携</span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>
      {isOpen && (
        <div className="space-y-4 pt-4">
        {/* 世界観選択 */}
        {savedWorlds.length > 0 && (
          <div>
            <Label htmlFor="worldSelect">世界観を選択</Label>
            <Select value={selectedWorldId || 'none'} onValueChange={(value) => {
              onWorldChange(value === 'none' ? '' : value)
            }}>
              <SelectTrigger>
                <SelectValue placeholder="世界観を選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {savedWorlds.map(world => (
                  <SelectItem key={world.id} value={world.id}>
                    {world.result.name} {world.result.genres?.length > 0 ? `(${world.result.genres.join(', ')})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedWorldId && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                <div className="text-blue-700">
                  {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                </div>
              </div>
            )}
          </div>
        )}

        {/* コミュニティ選択（複数） */}
        {communities.length > 0 && (
          <div>
            <Label>コミュニティを選択（複数可）</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {communities.filter(community => community?.result?.basicInfo).map(community => (
                <div key={community.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`community-${community.id}`}
                    checked={selectedCommunityIds.includes(community.id)}
                    onCheckedChange={() => handleCommunityToggle(community.id)}
                  />
                  <label
                    htmlFor={`community-${community.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">{community.result.basicInfo.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({community.result.basicInfo.type} / 人口: {community.result.basicInfo.population?.toLocaleString()}人)
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedCommunityIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedCommunityIds.length}個のコミュニティを選択中
              </p>
            )}
          </div>
        )}

        {/* ソサエティ選択（複数） */}
        {societies.length > 0 && (
          <div>
            <Label>組織を選択（複数可）</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {societies.filter(society => society?.result?.basicInfo).map(society => (
                <div key={society.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`society-${society.id}`}
                    checked={selectedSocietyIds.includes(society.id)}
                    onCheckedChange={() => handleSocietyToggle(society.id)}
                  />
                  <label
                    htmlFor={`society-${society.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">{society.result.basicInfo.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({society.result.basicInfo.type} / 規模: {society.result.basicInfo.scale})
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedSocietyIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedSocietyIds.length}個の組織を選択中
              </p>
            )}
          </div>
        )}

        {/* ダンジョン選択 */}
        {dungeons.length > 0 && (
          <div>
            <Label htmlFor="dungeonSelect">ダンジョンを選択</Label>
            <Select value={selectedDungeonId || 'none'} onValueChange={(value) => {
              onDungeonChange(value === 'none' ? '' : value)
            }}>
              <SelectTrigger>
                <SelectValue placeholder="ダンジョンを選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {dungeons.map(dungeon => (
                  <SelectItem key={dungeon.id} value={dungeon.id}>
                    {dungeon.result.basicInfo.name} ({dungeon.result.basicInfo.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDungeonId && (
              <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                <div className="font-medium text-purple-900 mb-1">選択中のダンジョン</div>
                <div className="text-purple-700">
                  {dungeons.find(d => d.id === selectedDungeonId)?.result.basicInfo.description?.substring(0, 100)}...
                </div>
              </div>
            )}
          </div>
        )}

        {/* キャラクター選択 */}
        {characters.length > 0 && (
          <div>
            <Label>登場キャラクター</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {characters.filter(character => character?.result?.name).map(character => (
                <div key={character.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`char-${character.id}`}
                    checked={selectedCharacterIds.includes(character.id)}
                    onCheckedChange={() => handleCharacterToggle(character.id)}
                  />
                  <label
                    htmlFor={`char-${character.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">{character.result.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({character.result.age}歳 / {character.result.role})
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedCharacterIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedCharacterIds.length}名のキャラクターを選択中
              </p>
            )}
          </div>
        )}

        {/* アイテム選択 */}
        {items.length > 0 && (
          <div>
            <Label>使用アイテム</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {items.filter(item => item?.result?.basicInfo).map(item => (
                <div key={item.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`item-${item.id}`}
                    checked={selectedItemIds.includes(item.id)}
                    onCheckedChange={() => handleItemToggle(item.id)}
                  />
                  <label
                    htmlFor={`item-${item.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">{item.result.basicInfo.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({item.result.basicInfo.type} / {item.result.basicInfo.rarity})
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedItemIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedItemIds.length}個のアイテムを選択中
              </p>
            )}
          </div>
        )}

        {/* エネミー選択 */}
        {enemies.length > 0 && (
          <div>
            <Label>登場する敵・脅威</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {enemies.filter(enemy => enemy?.result?.basicInfo).map(enemy => (
                <div key={enemy.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`enemy-${enemy.id}`}
                    checked={selectedEnemyIds.includes(enemy.id)}
                    onCheckedChange={() => handleEnemyToggle(enemy.id)}
                  />
                  <label
                    htmlFor={`enemy-${enemy.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">{enemy.result.basicInfo.name}</span>
                    <span className="text-muted-foreground ml-2">
                      ({enemy.result.basicInfo.type} / 脅威度: {enemy.result.combat?.threatLevel || '不明'})
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedEnemyIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedEnemyIds.length}体の敵を選択中
              </p>
            )}
          </div>
        )}

        {/* 関係性選択 */}
        {relationships.length > 0 && (
          <div>
            <Label>関係性設定</Label>
            <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
              {relationships.filter(relationship => relationship?.result).map(relationship => (
                <div key={relationship.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`rel-${relationship.id}`}
                    checked={selectedRelationshipIds.includes(relationship.id)}
                    onCheckedChange={() => handleRelationshipToggle(relationship.id)}
                  />
                  <label
                    htmlFor={`rel-${relationship.id}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    <span className="font-medium">
                      {relationship.title || '無題の関係性'}
                    </span>
                    <span className="text-muted-foreground ml-2">
                      ({relationship.result.characters?.length || 0}名の登場人物)
                    </span>
                  </label>
                </div>
              ))}
            </div>
            {selectedRelationshipIds.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedRelationshipIds.length}個の関係性を選択中
              </p>
            )}
          </div>
        )}

        {/* 説明 */}
        <div className="text-xs text-muted-foreground p-3 bg-muted rounded-md">
          選択した要素はシーン生成時に反映されます。
          世界観は全体の雰囲気に、コミュニティは舞台となる場所として、
          組織は登場する組織として、ダンジョンは特殊な舞台設定として、
          キャラクターは登場人物として、アイテムは小道具として、
          関係性は人物間の描写に活用されます。
        </div>
        </div>
      )}
    </div>
  )
}